import React, {useEffect, useMemo, useState} from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import {useTranslation} from 'react-i18next'
import useStyles from 'pages/ProductOperationOrganization/Component/DialogActiveProductStyle'
import useAuth from 'features/auth/useAuth'
import {changeLicenseStatus} from 'api/productApi'
import {LICENSE_STATUS, LicenseStatus} from '../constant'

type DialogActiveProductProps = {
  selectedLicense: {
    orgName: string
    productName: string
    licenseId: string
    statusLicense: LicenseStatus
  }
  open: boolean
  onClose: () => void
  onUpdateList: () => void
}

const DialogActiveProduct = (props: DialogActiveProductProps) => {
  const {open, onClose, selectedLicense, onUpdateList} = props
  const classes = useStyles()
  const {t} = useTranslation()
  const {onOpen: openFailModal} = useFailureModal()
  const {onOpen: openSuccessModal} = useSuccessModal()

  const {user: currentUser} = useAuth()

  const LIST_REASON = [
    {
      label: 'IRefund',
      value: 'Refund',
    },
    {
      label: 'IByUser',
      value: 'ByUser',
    },
    {
      label: 'IPaymentDelays',
      value: 'DelayPayment',
    },
    {
      label: 'IServiceImprovements',
      value: 'ServiceImprovement',
    },
    {
      label: 'IOther',
      value: 'Etc',
    },
  ]

  const statusRadio = useMemo(
    () => [
      ...(selectedLicense.statusLicense === LICENSE_STATUS.ACTIVE
        ? [
            {
              id: LICENSE_STATUS.INACTIVE as LicenseStatus,
              name: t('IPause'),
            },
          ]
        : []),
      ...(selectedLicense.statusLicense === LICENSE_STATUS.INACTIVE
        ? [
            {
              id: LICENSE_STATUS.ACTIVE as LicenseStatus,
              name: t('IOperationsResumed'),
            },
          ]
        : []),
      {
        id: LICENSE_STATUS.DESTROYED as LicenseStatus,
        name: `${t('IDestroyed')} (${t('IOperationCantResume')})`,
      },
    ],
    [selectedLicense, t],
  )

  const [privateUse, setPrivateUse] = useState<string>(LIST_REASON[0].value)
  const [reason, setReason] = useState<string>('')
  const [currentStatus, setCurrentStatus] = useState<LicenseStatus>(
    statusRadio[0].id,
  )

  const handleConfirm = async () => {
    try {
      const payload = {
        data: {
          status: currentStatus,
          reason,
          reasonCode: privateUse,
          changedByName: currentUser?.id?.toString() || '',
        },
        query: {
          licenseId: selectedLicense.licenseId,
        },
      }

      const response = await changeLicenseStatus(payload)

      if (response.code === '0') {
        onClose()
        setReason('')
        setPrivateUse(LIST_REASON[0].value)
        openSuccessModal(t('IProcessSuccess')).then(() => {
          onUpdateList()
        })
      }
    } catch (error) {
      onClose()
      openFailModal(t('IProcessFail'))
    }
  }

  const getDialogTitle = (statusLicense: LicenseStatus) => {
    switch (statusLicense) {
      case LICENSE_STATUS.ACTIVE:
        return t('IProductOperation')
      case LICENSE_STATUS.INACTIVE:
        return t('IOperationsResumed')
      default:
        return ''
    }
  }
  useEffect(() => {
    setCurrentStatus(statusRadio[0].id)
  }, [statusRadio])

  return (
    <Dialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.closeButtonWrap}>
        <IconButton
          color='secondary'
          aria-label='favorite'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <DialogContent className={classes.contentWrap}>
        <div className={classes.headerDialog}>
          {getDialogTitle(selectedLicense.statusLicense)}
        </div>
        <div className={classes.infoCard}>
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>
              {t('ICustomerOrganizationName')}:
            </span>
            <span className={classes.infoValue}>{selectedLicense.orgName}</span>
          </div>
          <div className={classes.infoItem}>
            <span className={classes.infoLabel}>
              {t('IProductNameSelect')}:
            </span>
            <span className={classes.infoValue}>
              {selectedLicense.productName}
            </span>
          </div>
        </div>
        <RadioGroup className={classes.groupRadio}>
          {statusRadio.map((item) => {
            return (
              <label key={item.id} htmlFor={item.id}>
                <Radio
                  color='primary'
                  id={item.id}
                  name={item.id}
                  value={item.id}
                  checked={currentStatus === item.id}
                  onChange={() => setCurrentStatus(item.id)}
                />
                {t(item.name)}
              </label>
            )
          })}
        </RadioGroup>
        <div
          style={{
            marginBottom: 20,
          }}
        >
          <span>{t('ICustomerManager')} :</span>{' '}
          <span>{currentUser?.name || ''}</span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span>{t('IPrivateUse')}:</span>

          <FormControl variant='outlined' style={{width: '86%'}}>
            <Select
              value={privateUse}
              onChange={(event: React.ChangeEvent<{value: unknown}>) =>
                setPrivateUse(event.target.value as KeyOfEdfSearchKind)
              }
              className={classes.searchSelect}
              input={<OutlinedInput name='age' id='outlined-age-simple' />}
            >
              {LIST_REASON.map((item) => (
                <MenuItem value={item.value} key={item.value}>
                  {t(item.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{marginTop: '13px'}}>
          <span
            style={{
              marginRight: 5,
            }}
          >
            {t('IPrivateInternal')}:
          </span>
          <input
            className={classes.input}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className={classes.listBtn}>
          <Button color='default' variant='contained' onClick={() => onClose()}>
            {t('ICancel')}
          </Button>
          <Button color='primary' variant='contained' onClick={handleConfirm}>
            {t('IConfirm')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DialogActiveProduct
